<template>
    <!-- global errors -->
    <div class="alert-box error" v-if="error" v-html="errorMessage"></div>
    <loader v-if="loading" :size="50" :border-width="16" class="tw-my-8" />
    <div
        v-if="!loading && !error && !successBooking"
        class="modal-content-wrapper tw-mx-auto tw-scroll-mt-[20px] tw-px-0 md:tw-px-10"
    >
        <div class="checkout-disclaimer divider-bottom tw-pb-4">{{ translate('booking.time_left') }}: <timer /></div>
        <date-time-preview
            v-if="showDateTimePreview"
            :active-view="activeView"
            @edit-date="changeView(BOOKING_VIEWS.CALENDAR, true)"
            @edit-time="changeView(BOOKING_VIEWS.TIME_PICKER, true)"
        />
        <keep-alive>
            <component
                :is="activeView"
                :key="activeView"
                :edit="viewEdit"
                @change-view="changeView($event)"
                @error="error = $event"
                @success-booking="successBooking = true"
            ></component>
        </keep-alive>
    </div>
    <transition name="fade">
        <div v-if="successBooking" class="modal-content-wrapper">
            <img :src="successfulPayment" alt="Successful Payment" class="tw-block tw-max-w-[144px] tw-mx-auto" />
            <h4 class="form-section-title tw-text-center tw-my-4 tw-text-xl tw-leading-6">
                {{ translate('booking.success.congratulations_title') }}
            </h4>
            <p class="disclaimer disclaimer-center tw-mt-6">{{ translate('booking.success.congratulations_text') }}</p>
            <p
                class="disclaimer disclaimer-center"
                v-html="translate('booking.success.transportation', { phone: phoneLink, email: emailLink })"
            ></p>
            <p class="disclaimer disclaimer-center tw-mt-6 tw-mb-8">{{ translate('booking.success.enjoy') }}</p>
            <div class="tw-mt-4 btn-container center">
                <a
                    href="https://book.atobtransfer.com/book?atob_ref=AUTHLAND"
                    target="_blank"
                    class="btn btn-primary btn-md btn-fw"
                >
                    {{ translate('booking.success.cta_text') }}
                </a>
            </div>
        </div>
    </transition>
</template>

<script>
import clockIcon from '@images/clock-circular-outline.svg';
import calendarIcon from '@images/calendar.svg';
import successfulPayment from '@images/successful-payment.png';
import { LocalDate, DateTimeFormatter } from '@js-joda/core';
import i18n from '@js/vue/mixins/i18n';
import groupPeopleIcon from '@images/group-people.svg';
import Calendar from './Calendar.vue';
import Offers from './Offers.vue';
import CustomerInfoAndPayment from './CustomerInfoAndPayment.vue';
import DateTimePreview from './shared/DateTimePreview.vue';
import Loader from './shared/Loader.vue';
import Timer from './shared/Timer.vue';
import TimePicker from './TimePicker.vue';
import { BOOKING_VIEWS } from '@js/vue/booking-views.js';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { trackBookingDateEdit, trackBookingTimeEdit } from '@js/vue/dataLayerUtils.js';
import { contactInfo } from '@js/vue/contact-info.js';

export default {
    name: 'Booking',
    components: {
        Calendar,
        TimePicker,
        Offers,
        CustomerInfoAndPayment,
        DateTimePreview,
        Loader,
        Timer,
    },
    inject: ['lang', 'translations'],
    mixins: [i18n],
    props: {
        id: {
            type: Number,
            required: true,
        },
        region: {
            type: Object,
            required: true,
        },
        category: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            successBooking: false,
            error: '',
            activeView: '',
            viewEdit: false,
            dateDisplayedFormat: 'dd.MM.yyyy',
            calendarIcon,
            clockIcon,
            successfulPayment,
            groupPeopleIcon,
            BOOKING_VIEWS,
            phoneLink: `<a class="tw-whitespace-nowrap tw-underline" href="tel:${contactInfo.phoneNumber}">${contactInfo.phoneNumber}</a>`,
            emailLink: `<a class="tw-whitespace-nowrap tw-underline" href="mailto:${contactInfo.emailAddress}">${contactInfo.experiences}</a>`,
        };
    },
    created() {
        document.addEventListener('modal:opened', this.openModal, false);
    },
    methods: {
        ...mapActions('booking', ['fetchBookingData']),
        ...mapMutations('booking', ['SET_REGION_CATEGORY']),
        openModal() {
            this.fetchBookingData(this.id)
                .then(() => {
                    this.changeView(BOOKING_VIEWS.CALENDAR);
                    this.loading = false;
                    let tempObject = {};
                    if (this.region.hasOwnProperty('slug')) tempObject['region-slug'] = this.region['slug'];
                    if (this.category.hasOwnProperty('slug')) tempObject['category-slug'] = this.category['slug'];
                    this.SET_REGION_CATEGORY(tempObject);
                })
                .catch((error) => {
                    console.error('An error occurred:', error);
                    this.error = 'something_went_wrong';
                });
        },
        changeView(name, isEdit = false) {
            if (isEdit && name === BOOKING_VIEWS.CALENDAR) {
                trackBookingDateEdit(this.title);
            } else if (isEdit && name === BOOKING_VIEWS.TIME_PICKER) {
                trackBookingTimeEdit(this.title);
            }
            this.viewEdit = isEdit;
            this.activeView = name;
        },
        formatDate(date) {
            const pattern = DateTimeFormatter.ofPattern(this.dateDisplayedFormat);
            return LocalDate.parse(date).format(pattern);
        },
    },
    computed: {
        ...mapGetters('booking', ['getServiceTitle']),
        showDateTimePreview() {
            return (
                this.activeView !== BOOKING_VIEWS.CALENDAR &&
                this.activeView !== BOOKING_VIEWS.CUSTOMER_INFO_AND_PAYMENT
            );
        },
        title() {
            return this.getServiceTitle;
        },
        errorMessage() {
            return this.translate(`booking.errors.${this.error}`);
        },
    },
};
</script>
